import React, { Component } from 'react';
import {Theme} from "@material-ui/core";
// @ts-ignore
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import { Link } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';

import ThermalReliabilityCard from './Calculator/ThermalReliabilityCard';
import ThermalAdjustedFITCard from './Calculator/ThermalAdjustedFITCard';

const styles = (theme: Theme) => ({
    root: {
        width: '100%',
        fontFamily: theme.typography.fontFamily,
        margin: `${theme.spacing(1)}px 0px`,
        color: theme.palette.text.secondary,
    },
    subcomponent: {
        padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    },
    subcomponentContainer: {
        display: 'inline-flex',
        background: theme.palette.type === 'dark' ? theme.palette.background.default : 'rgba(230,230,230, 0.7)',
    },
    colorInherit: {
        color: 'inherit'
    }
});

export interface ELFR_DPPM_JSON {
    fails: boolean;
    ELFR_DPPM: number;
    test_temp: number;
    conf_level: number;
    sample_size: number;
}

export interface MTBF_FIT_DATA_JSON {
    fit: number;
    MTBF: number;
    fails: boolean;
    test_temp: number;
    conf_level: number;
    usage_temp: number;
    sample_size: number;
    test_duration: number;
    activation_energy: number;
}

export interface PartsDataJSON {
    id: number;
    name: string;
    type: string;
    date_created: string;
    failure_data: {
        ELFR_data: ELFR_DPPM_JSON,
        MTBF_FIT_data: MTBF_FIT_DATA_JSON
    };
    data: object;
}

interface Props {
    data: PartsDataJSON;
    dataTotal: number | null;
    paginationEnabled: boolean;
    isLoading: boolean;
    fetchData: any;
    isPicker?: boolean;
    doUpdateSelectedPart?: (selectedPart: PartsDataJSON) => void;
    classes: any;
}

interface State {
    selected: number;
    pageSize: number;
    pageIndex: number;
}

class ComponentReactTable extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selected: null,
            pageSize: 5,
            pageIndex: 0
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        const { fetchData, paginationEnabled } = this.props;
        const { pageIndex, pageSize } = this.state;
        if (paginationEnabled && prevState.pageIndex !== pageIndex || prevState.pageSize !== pageSize) {
            fetchData(pageSize, pageIndex * pageSize)
        }
    }

    render() {
        const { classes, data, dataTotal, paginationEnabled, isLoading, isPicker, doUpdateSelectedPart } = this.props;
        const { pageSize, pageIndex } = this.state;

        const columns = [
            {
                Header: 'Details',
                columns: [
                    {
                        Header: 'Part Name',
                        id: 'name',
                        accessor: (d: PartsDataJSON) => (
                            <Link
                                to={{
                                    pathname: `/part/${d.id}/`,
                                    state: { details: d },
                                }}
                                onClick={isPicker ? (e) => e.preventDefault() : null}
                                className={classes.colorInherit}
                            >
                                {d.name}
                            </Link>
                        ),
                    },
                    {
                        Header: 'Part Type',
                        id: 'type',
                        accessor: (d: PartsDataJSON) => (
                            <Link
                                to={{
                                    pathname: `/part/${d.id}/`,
                                    state: { details: d },
                                }}
                                onClick={isPicker ? (e) => e.preventDefault() : null}
                                className={classes.colorInherit}
                            >
                                {d.type}
                            </Link>
                        ),
                    },
                    {
                        Header: 'FIT',
                        id: 'fit',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.fit,
                    },
                    {
                        Header: 'Activation Energy (eV)',
                        id: 'act_energy',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.activation_energy,
                    },
                ],
            },
            {
                Header: 'MTBF Rate',
                columns: [
                    {
                        Header: 'Fails',
                        id: 'mtbf_fails',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.fails,
                    },
                    {
                        Header: 'MTBF',
                        id: 'mtbf',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.MTBF,
                    },
                    {
                        Header: 'Test Temp (C)',
                        id: 'mtbf_test_temp',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.test_temp,
                    },
                    {
                        Header: 'Confidence Level (%)',
                        id: 'mtbf_conf_lvl',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.conf_level,
                    },
                    {
                        Header: 'Usage Temp (C)',
                        id: 'mtbf_use_temp',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.usage_temp,
                    },
                    {
                        Header: 'Sample Size',
                        id: 'mtbf_sample_size',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.sample_size,
                    },
                    {
                        Header: 'Test Duration (hrs)',
                        id: 'mtbf_test_duration',
                        accessor: (d: PartsDataJSON) => d.failure_data.MTBF_FIT_data.test_duration,
                    },
                ],
            },
            {
                Header: 'Early Life Failure Rate',
                columns: [
                    {
                        Header: 'Fails',
                        id: 'elfr_fails',
                        accessor: (d: PartsDataJSON) => d.failure_data.ELFR_data.fails,
                    },
                    {
                        Header: 'DPPM',
                        id: 'elfr_dppm',
                        accessor: (d: PartsDataJSON) => d.failure_data.ELFR_data.ELFR_DPPM,
                    },
                    {
                        Header: 'Test Temp (C)',
                        id: 'elfr_test_temp',
                        accessor: (d: PartsDataJSON) => d.failure_data.ELFR_data.test_temp,
                    },
                    {
                        Header: 'Confidence Level (%)',
                        id: 'elfr_conf_lvl',
                        accessor: (d: PartsDataJSON) => d.failure_data.ELFR_data.conf_level,
                    },
                    {
                        Header: 'Sample Size',
                        id: 'elfr_sample_size',
                        accessor: (d: PartsDataJSON) => d.failure_data.ELFR_data.sample_size,
                    },
                ],
            },
        ];

        return (
            <Paper className={classes.root}>
                <ReactTable
                    //@ts-ignore
                    data={data}
                    manual={paginationEnabled === true}
                    page={pageIndex}
                    pages={paginationEnabled === true ? Math.ceil(dataTotal/pageSize) : undefined}
                    loading={isLoading}
                    onPageChange={(pageIndex) => this.setState({ pageIndex })}
                    onPageSizeChange={(pageSize, pageIndex) => this.setState({ pageSize, pageIndex })}
                    filterable={!isPicker}
                    noDataText="Failed to load data"
                    columns={columns}
                    defaultPageSize={pageSize}
                    className="-striped -highlight"
                    SubComponent={(row: any) => {
                        const calcData = {
                            partId: row.row.name,
                            useTemp: row.row.mtbf_use_temp,
                            testTemp: row.row.mtbf_test_temp,
                            actEnergy: row.row.act_energy,
                            testDuration: row.row.mtbf_test_duration,
                            numFailures: row.row.mtbf_fails,
                            sampleSize: row.row.mtbf_sample_size,
                            confidence: row.row.mtbf_conf_lvl,
                        };
                        if (!isPicker) {
                            return (
                                <div className={classes.subcomponentContainer}>
                                    <div className={classes.subcomponent}>
                                        <ThermalAdjustedFITCard data={calcData} />
                                    </div>
                                    <div className={classes.subcomponent}>
                                        <ThermalReliabilityCard data={calcData} />
                                    </div>
                                </div>
                            );
                        }
                    }}
                    getTrProps={(state: any, rowInfo: { row: any; index: number; }) => {
                        if (rowInfo && rowInfo.row && isPicker) {
                            return {
                                onClick: () => {
                                    this.setState({ selected: rowInfo.index }, () => {
                                        isPicker && doUpdateSelectedPart(state.data[rowInfo.index]);
                                    });
                                },
                                style: {
                                    background: rowInfo.index === this.state.selected ? '#276C95' : 'white',
                                    color: rowInfo.index === this.state.selected ? 'white' : 'black'
                                }
                            }
                        }else{
                            return {}
                        }
                    }}
                />
            </Paper>
        );
    }
}

export default withStyles(styles)(ComponentReactTable);
